import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { DashboardBidPositiveCCF } from 'Douleutaras/views/client-dashboard/components/DashboardBidPositiveCCF';
import ContactModal from 'Douleutaras/functional-components/ccf/ContactModal';
import { PhoneNumberRevealedSource } from 'Douleutaras/app/lib/posthog/posthog.types';
import {
  ContactModalOptions,
  MODAL_BY_MILESTONE_POSITION
} from 'Douleutaras/constants/ccf/need-help-options';
import { getNeedHelpSave } from 'Douleutaras/redux/data/ccf/needHelpOptionsSlice';
import { CustomerFlowSlug } from 'Douleutaras/functional-components/ccf/CCFModals.types';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { useProGenderInfo } from 'Douleutaras/hooks/users/pro';
import { fetchJob, jobSelectors } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { fetchJobBids, jobBidsSelectors } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import { selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import { usersSelector } from 'Douleutaras/redux/data/users/usersSlice';
import { useCurrentMilestone } from 'Douleutaras/hooks/bids/currentMilestone';
import { useReviewSupport } from 'Douleutaras/hooks/bids/reviewSupport';

import { BidPositiveCcfSectionProps } from './types';
import { CtaSectionContent } from './CtaSectionContent';

export const BidPositiveCcfSection: React.FC<BidPositiveCcfSectionProps> = props => {
  const { sellerId, jobId, bidId, onPrimaryClicked } = props;

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const user = useSelector(selectCurrentUser);
  const sellerGenderInfo = useProGenderInfo(sellerId);
  const { currentStep, buyerSelectedMilestone } = useCurrentMilestone(bidId);
  const { reviewPosted, reviewAllowed, disableCCFPositive, isChatEmpty, conversationId } = useReviewSupport(jobId, bidId, sellerId);

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const job = useAppSelector(state => jobSelectors.selectById(state, parseInt(jobId, 0)));
  const bid = useAppSelector(state => jobBidsSelectors.selectById(state, bidId));
  const seller = useAppSelector(state => usersSelector.selectById(state, sellerId));

  useEffect(() => {
    if (job === undefined) {
      const jobIncludes = ['hired_seller'];
      if (user) {
        // If user is loggedIn then we can fetch information about buyer.
        jobIncludes.push('buyer');
      }
      dispatch(fetchJob({ jobId, includes: jobIncludes }));
    }
    if (bid === undefined) {
      dispatch(fetchJobBids({ jobId, includes: [ 'seller', 'feedback', 'book_now_entry', 'conversation', 'update_from_buyer',
          'price_defining_answers' ]}));
    }
  }, []);

  return (
    <>
      <CtaSectionContent header={t('T.PROS-PROFILE.CCF.REQUEST-A-QUOTE-FROM {{name}}', { name: seller?.attributes.firstName })}
                         description={t('T.PROS-PROFILE.CCF.IF-YOU-WANT-CHAT-OR-CALL-FOR-QUOTE')}
                         primaryBtn={
                           <DashboardBidPositiveCCF
                             reviewPosted={reviewPosted}
                             reviewAllowed={reviewAllowed}
                             disableCCFPositive={disableCCFPositive}
                             milestonePosition={currentStep?.position}
                             defaultAction={() => {
                               typeof onPrimaryClicked === 'function' && onPrimaryClicked();
                               setModalOpen(true);
                             }}
                             jobId={jobId}
                             bidId={bidId}
                             dateUnformatted={currentStep?.dateUnformatted}
                             isChatEmpty={isChatEmpty}
                             index={0}
                             sellerId={sellerId}
                             buyerSelectedMilestone={buyerSelectedMilestone}
                           />
                         }
      />
      <ContactModal isOpen={modalOpen}
                    source={PhoneNumberRevealedSource.EXPRESS_YOUR_INTEREST}
                    options={{
                      [ContactModalOptions.I_WILL_CALL_THE_PRO]: {
                        active: true
                      },
                      [ContactModalOptions.I_WILL_USE_THE_CHAT]: {
                        active: true
                      }
                    }}
                    conversationId={conversationId}
                    sellerPhoneNumber={seller?.attributes.phone}
                    setIsOpen={setModalOpen}
                    title={<Trans values={sellerGenderInfo}>
                      T.CCF.POSITIVE FLOW MODAL TITLE</Trans>}
                    note={<Trans values={sellerGenderInfo}>
                      T.CCF.POSITIVE FLOW MODAL BODY</Trans>}
                    onOptionSelected={(option: ContactModalOptions) => {
                      dispatch(getNeedHelpSave({
                        jobId,
                        bidId,
                        buyerChoice: option,
                        journeyStage: MODAL_BY_MILESTONE_POSITION[2],
                        customerFlow: CustomerFlowSlug.POSITIVE
                      }));
                    }}
                    sellerId={sellerId}
      />
    </>
  );
};
